// Shared marketplace UI primitives (status pills, cards, pickers, badges).
import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Anchor, Truck, MapPin, Ruler, DollarSign, Star, ShieldCheck, Timer, Eye, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import RouteMap from "@/components/RouteMap";

// ---------- Shared bits ----------
export const easing = [0.22, 1, 0.36, 1];

export function StatusPill({ status }) {
  const map = {
    open: "bg-[#FF3B30] text-white",
    assigned: "bg-amber-500/90 text-black",
    completed: "bg-emerald-500/90 text-black",
    cancelled: "bg-zinc-700 text-zinc-300",
    expired: "bg-zinc-800 text-zinc-400",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.25em] font-black px-2 py-1 ${map[status] || map.open}`}>
      {status}
    </span>
  );
}

export function VehicleIcon({ type }) {
  if (type === "boat") return <Anchor className="w-4 h-4" />;
  return <Truck className="w-4 h-4" />;
}

export function useNow(stepMs = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), stepMs);
    return () => clearInterval(t);
  }, [stepMs]);
  return now;
}

export function CountdownChip({ deadline, status, testid = "bid-countdown" }) {
  const now = useNow();
  if (status !== "open" || !deadline) return null;
  const ms = new Date(deadline).getTime() - now;
  if (Number.isNaN(ms) || ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const label = h >= 48 ? `${Math.floor(h / 24)}d ${h % 24}h left` : `${h}h ${m}m left`;
  const tone =
    h < 2
      ? "text-[#FF3B30] border-[#FF3B30]/60 animate-pulse"
      : h < 24
        ? "text-amber-400 border-amber-500/40"
        : "text-zinc-400 border-white/10";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 border text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 ${tone}`}
      title="Bidding closes when this hits zero"
    >
      <Timer className="w-3 h-3" /> {label}
    </span>
  );
}

export function DatePicker({ value, onChange, testid, placeholder = "Pick a date", compact = false }) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`w-full flex items-center gap-2 bg-[#141416] border border-white/[0.08] hover:border-[#FF3B30] text-left transition-colors ${compact ? "p-3 text-sm" : "p-4"}`}
        >
          <CalendarIcon className="w-4 h-4 text-zinc-500 shrink-0" />
          <span className={selected ? "text-white" : "text-zinc-600"}>
            {selected ? format(selected, "MMM d, yyyy") : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#0F0F10] border-white/[0.1]" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")}
          className="text-white"
        />
      </PopoverContent>
    </Popover>
  );
}


export function AvailabilityBadge({ status = "available", blackoutUntil = null, testid = "availability-badge" }) {
  let label = "Available";  let cls = "text-emerald-400 border-emerald-500/40";
  let dot = "bg-emerald-400";
  if (blackoutUntil) {
    label = `Blackout until ${new Date(`${blackoutUntil}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    cls = "text-red-400 border-red-500/50";
    dot = "bg-red-400";
  } else if (status === "on_the_road") {
    label = "On the road";
    cls = "text-amber-400 border-amber-500/40";
    dot = "bg-amber-400";
  } else if (status === "unavailable") {
    label = "Not available";
    cls = "text-zinc-500 border-white/10";
    dot = "bg-zinc-500";
  }
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 border text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {label}
    </span>
  );
}

export function RequestCard({ r, to, testid, stats, unread = 0 }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block border border-white/[0.08] bg-[#0F0F10] hover:border-[#FF3B30] transition-all overflow-hidden"
    >
      {r.pickup_lat != null && r.delivery_lat != null && (
        <RouteMap
          pickup={{ lat: r.pickup_lat, lng: r.pickup_lng }}
          delivery={{ lat: r.delivery_lat, lng: r.delivery_lng }}
          height={130}
        />
      )}
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-bold">
              <VehicleIcon type={r.vehicle_type} /> {r.vehicle_type}
            </div>
            <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight leading-tight truncate">
              {r.title}
            </h3>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <UnreadDot count={unread} testid={`req-unread-${r.id}`} />
            <StatusPill status={r.status} />
            <CountdownChip deadline={r.bid_deadline} status={r.status} testid={`countdown-${r.id}`} />
          </div>
        </div>
        <div className="mt-4 space-y-1.5 text-sm text-zinc-400">
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-[#FF3B30]" />
            <span className="font-semibold text-white">{r.pickup_city}</span>
            <ArrowRight className="w-3 h-3 text-zinc-600" />
            <span className="font-semibold text-white">{r.delivery_city}</span>
          </div>
          {r.shipper_rating_count != null && (
            <div className="flex items-center gap-2" data-testid={`shipper-rep-${r.id}`}>
              <Star className="w-3.5 h-3.5 text-[#FF3B30]" fill="#FF3B30" />
              {r.shipper_rating_count > 0 ? (
                <span className="text-xs font-semibold text-zinc-300">
                  {r.shipper_rating_avg.toFixed(1)} <span className="text-zinc-500">({r.shipper_rating_count})</span>
                </span>
              ) : (
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">New shipper</span>
              )}
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">
                {"· "}{r.shipper_completed_jobs ?? 0} {(r.shipper_completed_jobs ?? 0) === 1 ? "haul" : "hauls"} done
              </span>
              {r.shipper_verified && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-emerald-400"
                  data-testid={`shipper-verified-${r.id}`}
                  title="ID + ownership verified by admin"
                >
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
          )}
          {r.distance_miles != null && (
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-bold pt-0.5">
              <span>{Math.round(r.distance_miles).toLocaleString()} mi</span>
              {r.duration_min != null && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span>~{Math.floor(r.duration_min / 60)}h {r.duration_min % 60}m</span>
                </>
              )}
            </div>
          )}
          {r.dimensions && (
            <div className="flex items-center gap-2">
              <Ruler className="w-3.5 h-3.5 text-zinc-600" />
              <span>{r.dimensions}</span>
            </div>
          )}
          {r.budget_usd != null && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5 text-zinc-600" />
              <span>Budget ${r.budget_usd.toLocaleString()}</span>
            </div>
          )}
          {stats && (
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-zinc-500 font-bold pt-0.5" data-testid={`req-stats-${r.id}`}>
              <span className="inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> {stats.views} {stats.views === 1 ? "view" : "views"}
              </span>
              <span className="text-zinc-700">·</span>
              <span>{stats.bid_count} {stats.bid_count === 1 ? "bid" : "bids"}</span>
              {stats.first_bid_min != null && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span>1st bid {formatMins(stats.first_bid_min)}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="mt-5 text-[11px] uppercase tracking-widest text-[#FF3B30] font-bold flex items-center gap-2 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
          View details <ArrowUpRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}


// ---------- Shipper: My Requests ----------
export function formatMins(min) {
  if (min == null) return "—";
  if (min < 60) return `in ${Math.max(1, Math.round(min))}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h < 48) return m ? `in ${h}h ${m}m` : `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d ${h % 24}h`;
}


export function UnreadDot({ count, testid }) {
  if (!count) return null;
  return (
    <span
      data-testid={testid}
      title={`${count} unread negotiation message${count === 1 ? "" : "s"}`}
      className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[9px] font-black leading-none animate-pulse"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}


export function BidStatusPill({ status }) {
  const map = {
    pending: "bg-white/10 text-zinc-300",
    accepted: "bg-emerald-500/90 text-black",
    rejected: "bg-zinc-800 text-zinc-500",
    withdrawn: "bg-zinc-800 text-zinc-500",
    countered: "bg-amber-500/90 text-black",
    declined: "bg-zinc-800 text-zinc-500",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.25em] font-black px-2 py-1 ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}


export function Skeletons() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border border-white/[0.08] bg-[#0F0F10] p-6 h-48 animate-pulse" />
      ))}
    </div>
  );
}
export function EmptyState({ title, body, cta }) {
  return (
    <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]">
      <h3 className="font-display text-2xl md:text-3xl font-black uppercase leading-tight">{title}</h3>
      <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">{body}</p>
      {cta && (
        <Link
          to={cta.to}
          className="mt-6 inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] py-3 px-5 transition-all"
        >
          {cta.label} <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}


// ---------- Form field helpers ----------
export function F({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">{label}</label>
      {children}
    </div>
  );
}

export function TextInput({ label, name, type = "text", value, onChange, placeholder, testid, required }) {
  return (
    <F label={label}>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4"
      />
    </F>
  );
}
